import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Toast } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { isMobile, isTablet } from '../../services/device/deviceService';
import vcService from '../../services/vcService';
import { signatureNamespace } from '../../services/SocketService';
import { useSelector } from 'react-redux';
import { VC_SIGN_STORAGE_KEYS } from '../../assets/constants';

const QR_VALIDITY_MS = 2 * 60 * 1000; // 2 minutes

const clearVCSignLocalStorage = () => {
  try {
    localStorage.removeItem(VC_SIGN_STORAGE_KEYS.QrInfoList);
    localStorage.removeItem(VC_SIGN_STORAGE_KEYS.RequestId);
    localStorage.removeItem(VC_SIGN_STORAGE_KEYS.SignerGuid);
    localStorage.removeItem(VC_SIGN_STORAGE_KEYS.Certificate);
    localStorage.removeItem(VC_SIGN_STORAGE_KEYS.paginationMeta);
  } catch (e) {
    // ignore
  }
};

const persistSessionToLocalStorage = () => {
  // Keep parity with existing mobile redirect patterns (sessionStorage may not survive app-switching)
  try {
    localStorage.setItem('AccessMethod', sessionStorage.getItem('AccessMethod'));
    localStorage.setItem('oauthTokenB64', sessionStorage.getItem('oauthTokenB64'));
    localStorage.setItem('appearanceConf', sessionStorage.getItem('appearanceConf'));
    localStorage.setItem('showApprovals', sessionStorage.getItem('showApprovals'));
    localStorage.setItem('user', sessionStorage.getItem('user'));
  } catch (e) {
    // ignore
  }
};

const VCSignature = ({ show, onClose, vidMobileUserGuid, signerGuid, certificateSelected, onWalletSignSuccess }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [sessionFlow, setSessionFlow] = useState(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const hasAutoRequestedRef = useRef(false);
  const qrTimeoutRef = useRef(null);

  const { paginationMeta } = useSelector(state => state.documents);
  
  const resetAndClose = () => {
    if (qrTimeoutRef.current) {
      clearTimeout(qrTimeoutRef.current);
      qrTimeoutRef.current = null;
    }
    setShowExpiredModal(false);
    setError(null);
    setQrImage(null);
    setSessionFlow(null);
    clearVCSignLocalStorage();
    onClose();
  };

  const runDesktopFlow = useCallback(async () => {
    setError(null);
    try {
      const body = {
        CertificateGuid: certificateSelected.GUID,
        CertificateType: certificateSelected.CertType,
        SignatureGuids: [signerGuid]
      };
      const response = await vcService.getSignatureQR(vidMobileUserGuid, body);
      const responseData = response && response.data ? response.data : {};
      const qrImageFromResponse = responseData.QR || null;
      const RequestId = responseData.RequestId;
      setQrImage(qrImageFromResponse);
      if (RequestId) {
        setRequestId(RequestId);
        signatureNamespace.emit('listenSignature', RequestId);
      }
    } catch (e) {
      const responseData = e && e.response && e.response.data ? e.response.data : null;
      const message = e && e.message ? e.message : null;
      setError(responseData || message || 'Error requesting wallet signature');
    }
  }, [certificateSelected, signerGuid, vidMobileUserGuid]);

  const runMobileFlow = useCallback(async () => {
    setError(null);
    try {
      const body = {
        CertificateGuid: certificateSelected.GUID,
        CertificateType: certificateSelected.CertType,
        SignatureGuids: [signerGuid]
      };
      const response = await vcService.getSignatureQR(vidMobileUserGuid, body);
      const responseData = response && response.data ? response.data : {};
      const qrImageFromResponse = responseData.QR || null;
      const RequestId = responseData.RequestId;

      persistSessionToLocalStorage();
      const singleQrList = qrImageFromResponse ? [{ Data: qrImageFromResponse }] : [];
      localStorage.setItem(VC_SIGN_STORAGE_KEYS.QrInfoList, JSON.stringify(singleQrList));
      if (RequestId) localStorage.setItem(VC_SIGN_STORAGE_KEYS.RequestId, RequestId);
      localStorage.setItem(VC_SIGN_STORAGE_KEYS.SignerGuid, signerGuid);
      localStorage.setItem(VC_SIGN_STORAGE_KEYS.Certificate, JSON.stringify(certificateSelected));
      localStorage.setItem(VC_SIGN_STORAGE_KEYS.paginationMeta, paginationMeta);
      localStorage.setItem('AccessMethod', sessionStorage.getItem('AccessMethod'));
      localStorage.setItem('oauthTokenB64', sessionStorage.getItem('oauthTokenB64'));
      localStorage.setItem('appearanceConf', sessionStorage.getItem('appearanceConf'));
      localStorage.setItem('showApprovals', sessionStorage.getItem('showApprovals'));
      localStorage.setItem('user', sessionStorage.getItem('user'));

      const redirectURL = responseData.Deeplink;
      onClose();
      window.location.href = redirectURL;
    } catch (e) {
      const responseData = e && e.response && e.response.data ? e.response.data : null;
      const message = e && e.message ? e.message : null;
      setError(responseData || message || 'Error requesting wallet signature');
    } finally {
      // no-op
    }
  }, [certificateSelected, onClose, paginationMeta, signerGuid, vidMobileUserGuid]);

  useEffect(() => {
    if (!show) {
      hasAutoRequestedRef.current = false;
      return;
    }
    if (hasAutoRequestedRef.current) return;
    hasAutoRequestedRef.current = true;

    const isMobileDevice = isMobile() || isTablet();
    setSessionFlow(isMobileDevice ? 'mobile' : 'desktop');
    if (isMobileDevice) {
      runMobileFlow();
    } else {
      runDesktopFlow();
    }
  }, [show, runDesktopFlow, runMobileFlow]);

  useEffect(() => {
    if (!show || sessionFlow !== 'desktop' || !qrImage) return;

    qrTimeoutRef.current = setTimeout(() => {
      qrTimeoutRef.current = null;
      setShowExpiredModal(true);
    }, QR_VALIDITY_MS);

    return () => {
      if (qrTimeoutRef.current) {
        clearTimeout(qrTimeoutRef.current);
        qrTimeoutRef.current = null;
      }
    };
  }, [show, sessionFlow, qrImage]);

  useEffect(() => {
    if (!show || sessionFlow !== 'desktop' || !requestId) return;
    const handleSignSuccess = (data) => {
      if (!data?.requestId) return;

      if (qrTimeoutRef.current) {
        clearTimeout(qrTimeoutRef.current);
        qrTimeoutRef.current = null;
      }
      onWalletSignSuccess(data.requestId, data.cidSuffix);
    };
    signatureNamespace.on('sign:success', handleSignSuccess);
    return () => {
      signatureNamespace.off('sign:success', handleSignSuccess);
    };
  }, [show, sessionFlow, requestId, onWalletSignSuccess]);

  return (
    <>
      <Modal
        show={show}
        onHide={resetAndClose}
        centered
        className="wallet-authorization-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('certificates.wallet-authorization-title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="wallet-auth-modal-body">
          {showExpiredModal ? (
            <p className="wallet-auth-expired-message">{t('certificates.qr-validation-time-expired')}</p>
          ) : sessionFlow === 'mobile' ? (
            <p>{t('certificates.wallet-redirecting')}</p>
          ) : sessionFlow === 'desktop' && qrImage ? (
            <div className="wallet-auth-content">
              <p className="wallet-auth-instructions">
                {t('certificates.qr-signature-instructions')}
              </p>
              <div className="qr-code-container">
                <img src={qrImage} alt="QR Code" className="qr-code-image" />
              </div>
            </div>
          ) : (
            <p>{t('certificates.wallet-authorization-loading-signature')}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" className="reject" onClick={resetAndClose}>
            {t('general.cancel')}
          </Button>
          {/* Auto-starts on open; no explicit Sign button. */}
        </Modal.Footer>
      </Modal>

      {error && (
        <Toast
          onClose={() => setError(null)}
          show={!!error}
          animation={true}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            margin: '0 auto',
            backgroundColor: 'rgba(255,255,255,1)',
            border: 'none',
            zIndex: 9999,
          }}
        >
          <Toast.Header closeButton={true}>
            <strong className="mr-auto">{t('general.error')}</strong>
          </Toast.Header>
          <Toast.Body>{error}</Toast.Body>
        </Toast>
      )}
    </>
  );
};

export const VC_SIGN_STORAGE = VC_SIGN_STORAGE_KEYS;

export default VCSignature;

